/**
 * @module components/analysis/UploadForm
 * @description
 *   Upload + metadata form for starting a music or book analysis: file picker,
 *   craft-specific metadata fields, lyrics/manuscript textarea, instrumental
 *   checkbox, and the submit button with upload/analysis progress states.
 *
 *   Music: Subgenre field is omitted when the parent has zero options (e.g. k_pop)
 *   so the UI does not look unfinished. Skip placeholder names the parent
 *   (`Optional — skip to use Hip Hop`).
 *
 * @extracted-from client/src/App.js (lines ~1253–1670, Phase 7)
 */

import React from 'react';
import {
  Upload,
  Mic,
  Settings,
  Users,
  Sparkles,
  AlertCircle,
  CheckCircle,
  BookOpen,
  FileText,
  Headphones,
} from 'lucide-react';
import analytics from '../../utils/analytics';
import {
  Field,
  Input,
  Textarea,
  Select,
  Button,
  Alert,
  Progress,
  CharacterMeter,
  Eyebrow,
} from '../ui';
import { MUSIC_SUBGENRES_BY_PARENT } from '../../constants/musicSubgenres';
import { formatMusicGenreDisplay } from '../../constants/musicGenreLabels';

export default function UploadForm({
  craftType,
  formData,
  onInputChange,
  onInstrumentalChange,
  selectedFile,
  onFileSelect,
  onSubmit,
  isLoading,
  uploadProgress,
  error,
}) {
  const isSubmitDisabled =
    isLoading ||
    (craftType === 'music'
      ? !selectedFile ||
        !formData.trackTitle.trim() ||
        !formData.artistName.trim() ||
        !formData.genre
      : !formData.bookTitle.trim() ||
        !formData.authorName.trim() ||
        !formData.genre ||
        !formData.targetAudience);

  const subgenreOptions = MUSIC_SUBGENRES_BY_PARENT[formData.genre] || [];

  return (
    <div className="critic-hero-surface animate-slide-up mt-10 md:mt-14 overflow-hidden">
      <div className="relative border-b border-white/[0.06] bg-gradient-to-b from-white/[0.035] to-transparent">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px critic-insight-line opacity-60" aria-hidden />
        <div className="relative px-6 pt-8 pb-6 md:px-10 md:pt-10 md:pb-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="text-left max-w-xl">
            <Eyebrow className="mb-2">Upload · metadata · run analysis</Eyebrow>
            <h3 className="font-display text-2xl md:text-3xl font-semibold text-critic-text-primary tracking-tight">
              Start your analysis
            </h3>
            <p className="text-sm md:text-base text-critic-text-secondary mt-3 leading-relaxed">
              Choose music or books above, upload your file, then complete the fields. Paste lyrics or
              manuscript text when you want deeper context — same flow for both crafts.
            </p>
          </div>
          <div
            className="flex items-end justify-start lg:justify-end gap-0.5 h-14 opacity-[0.85] shrink-0"
            aria-hidden="true"
          >
            {[6, 12, 9, 16, 11, 14, 8, 18, 10, 15, 7, 13, 9, 11].map((h, i) => (
              <span
                key={i}
                className="w-[3px] rounded-full bg-gradient-to-t from-apple-blue/25 to-critic-insight-lavender"
                style={{ height: `${h}px` }}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="relative p-6 md:p-10 lg:p-12">
        <form onSubmit={onSubmit} className="space-y-10">
          <Field
            label={`Upload ${craftType === 'music' ? 'Audio' : 'Book'} File`}
            required
            icon={<Upload className="h-5 w-5 text-apple-blue" />}
          >
            <div className="border-2 border-dashed border-white/[0.12] rounded-3xl p-10 md:p-12 text-center hover:border-apple-blue/45 transition-all duration-critic ease-critic-out group bg-gradient-to-b from-white/[0.04] to-transparent backdrop-blur-sm">
              <div className="relative mb-8">
                <div className="absolute inset-0 bg-gradient-to-r from-apple-blue/15 to-critic-insight-end/15 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-critic" />
                <Upload
                  className="relative mx-auto h-16 w-16 md:h-20 md:w-20 text-critic-text-muted group-hover:text-apple-blue transition-all duration-critic group-hover:scale-[1.04]"
                  aria-hidden
                />
              </div>
              <div className="space-y-4">
                <p className="text-critic-text-secondary group-hover:text-critic-text-primary transition-colors duration-critic text-base md:text-lg font-normal">
                  Drag and drop your {craftType === 'music' ? 'audio' : 'book'} file here, or click to
                  browse
                </p>
                <p className="text-sm text-critic-text-muted font-normal">
                  {craftType === 'music'
                    ? 'Supported formats: MP3, WAV, FLAC (max 25MB)'
                    : 'Supported formats: TXT, PDF, DOC, DOCX (max 10MB)'}
                </p>
                <input
                  type="file"
                  accept={craftType === 'music' ? '.mp3,.wav,.flac' : '.txt,.pdf,.doc,.docx'}
                  onChange={onFileSelect}
                  className="sr-only"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="btn-primary cursor-pointer inline-block mt-4 focus-within:ring-2 focus-within:ring-apple-blue focus-within:ring-offset-2 focus-within:ring-offset-critic-bg-deep rounded-2xl"
                  onClick={() => analytics.trackButtonClick('choose_file', 'upload_section')}
                >
                  Choose File
                </label>
              </div>
              {selectedFile && (
                <div className="mt-6 p-4 bg-gradient-to-r from-apple-success/20 to-apple-blue/20 backdrop-blur-xl rounded-2xl border border-apple-success/30 animate-bounce-in">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-apple-success" />
                    <div>
                      <p className="text-critic-text-primary font-normal">
                        <span className="text-critic-text-muted font-normal">Selected:</span>{' '}
                        {selectedFile.name}
                      </p>
                      <p className="text-sm text-critic-text-muted font-normal mt-1">
                        Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {craftType === 'music' ? (
              <>
                <Field
                  label="Track Title"
                  required
                  htmlFor="trackTitle"
                  icon={<Mic className="h-5 w-5 text-apple-blue" />}
                  className="space-y-0"
                >
                  <Input
                    id="trackTitle"
                    type="text"
                    name="trackTitle"
                    value={formData.trackTitle}
                    onChange={onInputChange}
                    placeholder="Enter track title"
                    maxLength={200}
                    required
                  />
                  <CharacterMeter
                    current={formData.trackTitle.length}
                    max={200}
                    barClassName="bg-gradient-to-r from-apple-blue to-apple-purple"
                  />
                </Field>
                <Field
                  label="Artist Name"
                  required
                  htmlFor="artistName"
                  icon={<Users className="h-5 w-5 text-apple-purple" />}
                >
                  <Input
                    id="artistName"
                    type="text"
                    name="artistName"
                    value={formData.artistName}
                    onChange={onInputChange}
                    placeholder="Enter artist name"
                    maxLength={200}
                    required
                  />
                  <CharacterMeter
                    current={formData.artistName.length}
                    max={200}
                    barClassName="bg-gradient-to-r from-apple-purple to-apple-pink"
                  />
                </Field>
              </>
            ) : (
              <>
                <Field
                  label="Book Title"
                  required
                  htmlFor="bookTitle"
                  icon={<BookOpen className="h-5 w-5 text-critic-insight-lavender" />}
                >
                  <Input
                    id="bookTitle"
                    type="text"
                    name="bookTitle"
                    value={formData.bookTitle}
                    onChange={onInputChange}
                    placeholder="Enter book title"
                    maxLength={200}
                    required
                  />
                  <CharacterMeter
                    current={formData.bookTitle.length}
                    max={200}
                    barClassName="bg-gradient-to-r from-apple-blue to-critic-insight-purple"
                  />
                </Field>
                <Field
                  label="Author Name"
                  required
                  htmlFor="authorName"
                  icon={<Users className="h-5 w-5 text-apple-purple" />}
                >
                  <Input
                    id="authorName"
                    type="text"
                    name="authorName"
                    value={formData.authorName}
                    onChange={onInputChange}
                    placeholder="Enter author name"
                    maxLength={200}
                    required
                  />
                  <CharacterMeter
                    current={formData.authorName.length}
                    max={200}
                    barClassName="bg-gradient-to-r from-apple-purple to-apple-pink"
                  />
                </Field>
              </>
            )}
          </div>

          {craftType === 'music' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Field
                label="Genre"
                required
                htmlFor="genre"
                icon={<FileText className="h-5 w-5 text-apple-orange" />}
              >
                <Select
                  id="genre"
                  name="genre"
                  value={formData.genre}
                  onChange={onInputChange}
                  required
                >
                  <option value="">Select genre</option>
                  <option value="pop">Pop</option>
                  <option value="hip_hop">Hip Hop</option>
                  <option value="electronic">Electronic</option>
                  <option value="r&b">R&B</option>
                  <option value="rock">Rock</option>
                  <option value="country">Country</option>
                  <option value="instrumental">Instrumental</option>
                  <option value="latin">Latin</option>
                  <option value="afrobeats">Afrobeats</option>
                  <option value="k_pop">K-Pop</option>
                  <option value="indie">Indie</option>
                </Select>
              </Field>
              {subgenreOptions.length > 0 && (
              <Field
                label="Subgenre"
                htmlFor="subgenre"
                icon={<FileText className="h-5 w-5 text-critic-text-muted" />}
              >
                <Select
                  id="subgenre"
                  name="subgenre"
                  value={formData.subgenre || ''}
                  onChange={onInputChange}
                >
                  <option value="">
                    Optional — skip to use {formatMusicGenreDisplay(formData.genre)}
                  </option>
                  {subgenreOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </Field>
              )}
            </div>
          )}

          {craftType === 'book' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Field
                label="Genre"
                required
                htmlFor="genre"
                icon={<FileText className="h-5 w-5 text-apple-orange" />}
              >
                <Select
                  id="genre"
                  name="genre"
                  value={formData.genre}
                  onChange={onInputChange}
                  required
                >
                  <option value="">Select genre</option>
                  <option value="fiction">Fiction</option>
                  <option value="non-fiction">Non-Fiction</option>
                  <option value="mystery">Mystery</option>
                  <option value="thriller">Thriller</option>
                  <option value="romance">Romance</option>
                  <option value="science-fiction">Science Fiction</option>
                  <option value="fantasy">Fantasy</option>
                  <option value="historical">Historical</option>
                  <option value="biography">Biography</option>
                  <option value="memoir">Memoir</option>
                  <option value="self-help">Self-Help</option>
                  <option value="business">Business</option>
                  <option value="other">Other</option>
                </Select>
              </Field>
              <Field
                label="Target Audience"
                required
                htmlFor="targetAudience"
                icon={<Users className="h-5 w-5 text-apple-indigo" />}
              >
                <Select
                  id="targetAudience"
                  name="targetAudience"
                  value={formData.targetAudience}
                  onChange={onInputChange}
                  required
                >
                  <option value="">Select target audience</option>
                  <option value="children">Children (Ages 3-12)</option>
                  <option value="young-adult">Young Adult (Ages 13-18)</option>
                  <option value="adult">Adult (Ages 18+)</option>
                  <option value="academic">Academic/Professional</option>
                  <option value="general">General Audience</option>
                </Select>
              </Field>
            </div>
          )}

          {craftType === 'music' ? (
            <div className="space-y-3">
              <Field
                label="Lyrics (Optional)"
                htmlFor="lyrics"
                icon={<Mic className="h-5 w-5 text-apple-green" />}
              >
                <Textarea
                  id="lyrics"
                  name="lyrics"
                  value={formData.lyrics}
                  onChange={onInputChange}
                  placeholder="Paste lyrics here for better analysis..."
                  rows={6}
                  className="resize-none"
                  maxLength={10000}
                />
                <CharacterMeter
                  current={formData.lyrics.length}
                  max={10000}
                  barClassName="bg-gradient-to-r from-apple-green to-apple-teal"
                />
              </Field>

              <div className="flex items-center space-x-3 p-4 bg-critic-bg-raised/80 rounded-xl border border-white/[0.08]">
                <input
                  type="checkbox"
                  name="isInstrumental"
                  id="isInstrumental"
                  checked={formData.isInstrumental || false}
                  onChange={(e) => onInstrumentalChange(e.target.checked)}
                  className="w-5 h-5 rounded border-white/20 bg-critic-bg-card text-apple-green focus:ring-2 focus:ring-apple-green focus:ring-offset-2 focus:ring-offset-critic-bg-deep cursor-pointer"
                />
                <label
                  htmlFor="isInstrumental"
                  className="text-critic-text-primary cursor-pointer flex items-center gap-3"
                >
                  <Headphones className="h-5 w-5 shrink-0 text-apple-green" aria-hidden />
                  <span className="font-sans font-medium text-base text-critic-text-primary leading-snug">
                    This is an instrumental track / beat
                  </span>
                </label>
              </div>
              {formData.isInstrumental && (
                <p className="text-sm text-critic-text-secondary font-normal">
                  Instrumental tracks are analyzed for production, mixing, and emotion only. Vocals and
                  lyrics scores are hidden.
                </p>
              )}
            </div>
          ) : (
            <Field
              label="Manuscript Content (Optional)"
              htmlFor="manuscript"
              icon={<FileText className="h-5 w-5 text-apple-blue" />}
              hint={
                !formData.manuscript.trim()
                  ? "No manuscript provided? We'll analyze based on genre and target audience for general guidance."
                  : undefined
              }
            >
              <Textarea
                id="manuscript"
                name="manuscript"
                value={formData.manuscript}
                onChange={onInputChange}
                placeholder="Paste your manuscript content here for enhanced analysis (optional)..."
                rows={8}
                className="resize-none"
                maxLength={50000}
              />
              <CharacterMeter
                current={formData.manuscript.length}
                max={50000}
                barClassName="bg-gradient-to-r from-apple-blue to-critic-insight-purple"
              />
            </Field>
          )}

          {error && (
            <Alert tone="error" title="Error" className="animate-bounce-in rounded-2xl p-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-10 h-10 bg-apple-error/20 rounded-xl flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-apple-error" />
                </div>
                <p className="text-critic-text-secondary">{error}</p>
              </div>
            </Alert>
          )}

          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitDisabled}
            className="w-full font-display text-lg font-bold py-6"
            onClick={() => analytics.trackButtonClick('analyze_review', 'form')}
          >
            {isLoading ? (
              <div className="w-full space-y-4">
                <div className="text-center mb-4">
                  <p className="font-display text-lg font-semibold text-critic-text-primary">
                    {uploadProgress < 30
                      ? `Uploading... ${Math.round(uploadProgress)}%`
                      : uploadProgress < 90
                        ? `${craftType === 'music' ? 'Analyzing audio' : 'Analyzing manuscript'}... ${Math.round(uploadProgress)}%`
                        : uploadProgress < 100
                          ? 'Finalizing...'
                          : 'Done'}
                  </p>
                  <p className="text-sm text-critic-text-secondary mt-1">
                    Please wait while we process your {craftType === 'music' ? 'track' : 'book'}
                  </p>
                </div>
                <Progress
                  value={uploadProgress}
                  className="h-3"
                  barClassName="bg-gradient-to-r from-apple-blue to-apple-purple"
                  aria-label="Upload progress"
                />
              </div>
            ) : (
              <div className="flex items-center justify-center space-x-4">
                <Settings className="h-6 w-6" />
                <span className="font-display text-xl">
                  Analyze & Review {craftType === 'music' ? 'Track' : 'Book'}
                </span>
                <Sparkles className="h-5 w-5 text-critic-text-muted" />
              </div>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
